/**
 * LeadDetailBilling
 * -----------------
 * Billing tab for the Lead Detail panel. Houses CurrentBillingPanel,
 * the "Charge a pack" matrix, and the "Other billing actions" CTAs
 * (Email enrollment link / One-off charge / Setup card on file).
 *
 * Extracted from CoachDashboard.jsx (2026-06-08) as part of the P3
 * leadDetail/{Info,Billing,Schedule} split. JSX is copied VERBATIM —
 * no styling or behavioral changes.
 *
 * 2026-02-13: SetupCardForClientButton extracted to a shared component
 * so the AthleteDossier billing tab can reuse it. Same UX/endpoints.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, CalendarDays, ClipboardList, Mail, Loader2, RefreshCw, RefreshCwOff } from "lucide-react";
import ChargeAndSubscribePanel from "@/components/coach/leadDetail/ChargeAndSubscribePanel";
import DemoModeToggle from "@/components/coach/leadDetail/DemoModeToggle";
import { LogPastAttendanceModal } from "@/components/coach/PackPricingMatrix";
import SetupCardForClientButton from "@/components/coach/SetupCardForClientButton";
import NoSubscriptionBanner from "@/components/coach/NoSubscriptionBanner";
import ActiveSubscriptionsPanel from "@/components/coach/leadDetail/ActiveSubscriptionsPanel";
import AutoRenewsPanel from "@/components/coach/leadDetail/AutoRenewsPanel";
import PaymentHistoryPanel from "@/components/coach/leadDetail/PaymentHistoryPanel";
import StripeSubscriptionsInspector from "@/components/coach/leadDetail/StripeSubscriptionsInspector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


function PackAutoRenewToggle({ userId, pack }) {
    const [busy, setBusy] = useState(false);
    // Only Member · Auto packs can auto-renew. Surface state from the
    // pack row's auto_renew_disabled_at flag (null = ON; iso str = OFF).
    const isAutoPack = String(pack.pack_key || "").startsWith("member_auto_");
    if (!isAutoPack) return null;
    const enabled = !pack.auto_renew_disabled_at;

    const toggle = async () => {
        const next = !enabled;
        const verb = next ? "Re-ENABLE auto-renew" : "DISABLE auto-renew";
        const msg = next
            ? `${verb}?\n\nWhen this pack's sessions run out, we'll auto-charge their card on file for a fresh pack of the same size.`
            : `${verb}?\n\nThis pack will run to completion (sessions still usable), but NO new pack will auto-purchase when it expires.\n\nThe client's current pack and any active subscription are NOT affected.`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            await axios.post(
                `${API}/coach/clients/${userId}/packs/${pack.pack_id}/auto-renew/toggle`,
                { enabled: next },
                { withCredentials: true },
            );
            toast.success(next ? "Auto-renew enabled" : "Auto-renew disabled");
            window.dispatchEvent(new Event("maxsc:lead-meta-refresh"));
        } catch (e) {
            toast.error("Couldn't toggle auto-renew", {
                description: e?.response?.data?.detail || "Unknown error",
            });
        } finally { setBusy(false); }
    };

    return (
        <button
            type="button"
            data-testid={`pack-auto-renew-toggle-${pack.pack_id}`}
            onClick={toggle}
            disabled={busy}
            title={enabled ? "Click to stop auto-charging when this pack runs out" : "Click to re-enable auto-renew on this pack"}
            className={`mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 border transition-colors disabled:opacity-50 ${
                enabled
                    ? "border-emerald-700/60 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/50"
                    : "border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:text-zinc-200"
            }`}
        >
            {busy ? (
                <Loader2 className="w-3 h-3 animate-spin" />
            ) : enabled ? (
                <RefreshCw className="w-3 h-3" />
            ) : (
                <RefreshCwOff className="w-3 h-3" />
            )}
            Auto-renew {enabled ? "ON" : "OFF"}
            <span className="normal-case tracking-normal text-[9px] opacity-70">
                · tap to turn {enabled ? "off" : "on"}
            </span>
        </button>
    );
}


function CurrentBillingPanel({ lead }) {
    const sub = lead.active_subscription;
    const packs = lead.active_packs || [];
    const totalRemaining = lead.credits_total_remaining ?? 0;
    const hasAnything = Boolean(sub) || packs.length > 0;
    const userId = lead.user_id;
    const fmtMoney = (cents) => cents == null ? "—" : `$${(cents / 100).toFixed(2)}`;
    const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
    const [logOpen, setLogOpen] = useState(false);
    const [localAthletes, setLocalAthletes] = useState(null); // self-athlete created on the fly
    const [ensuring, setEnsuring] = useState(null); // "log" | "attendance" | null
    const navigate = useNavigate();

    // ALWAYS show the log/attendance buttons (owner request 2026-06-16 —
    // they used to vanish for adult clients with no athlete records). If the
    // profile has no athletes yet, we idempotently create their self-athlete
    // on first use via /coach/clients/{id}/ensure-self-athlete.
    const athletes = localAthletes || (Array.isArray(lead.athletes) ? lead.athletes : []);

    const ensureAthletes = async () => {
        if (athletes.length > 0) return athletes;
        const r = await axios.post(
            `${API}/coach/clients/${lead.user_id}/ensure-self-athlete`,
            {},
            { withCredentials: true },
        );
        const created = [r.data.athlete];
        setLocalAthletes(created);
        window.dispatchEvent(new Event("maxsc:lead-meta-refresh"));
        return created;
    };

    const openLog = async () => {
        setEnsuring("log");
        try {
            await ensureAthletes();
            setLogOpen(true);
        } catch (e) {
            toast.error("Couldn't prepare athlete record", { description: e?.response?.data?.detail });
        } finally {
            setEnsuring(null);
        }
    };

    const openAttendance = async () => {
        setEnsuring("attendance");
        try {
            const list = await ensureAthletes();
            navigate(`/coach/athletes/${list[0].athlete_id}?tab=attendance`);
        } catch (e) {
            toast.error("Couldn't open attendance", { description: e?.response?.data?.detail });
        } finally {
            setEnsuring(null);
        }
    };

    return (
        <div data-testid="current-billing-panel" className="p-6 border-b border-zinc-900">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3 flex items-center gap-2">
                <Wallet className="w-3 h-3" /> Current plan & credits
            </div>
            {!hasAnything && (
                <div className="border border-dashed border-zinc-800 rounded-lg px-4 py-4 text-center mb-3">
                    <p className="text-sm text-zinc-300">
                        No active subscription or session packs yet.
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-1">
                        Use <span className="text-brand-red">Assign subscription / package</span> below to set one up.
                    </p>
                </div>
            )}
            <div className="space-y-3">
                    {sub && (
                        <div
                            data-testid="active-subscription-row"
                            className="border border-emerald-700/40 bg-emerald-900/10 px-4 py-3"
                        >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300">
                                        Active subscription
                                    </div>
                                    <div className="text-sm text-white font-semibold mt-1 truncate">
                                        {sub.plan_name || "Membership"}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-mono text-white">
                                        {fmtMoney(sub.amount_cents)}{sub.interval ? `/${sub.interval}` : ""}
                                    </div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300 mt-0.5">
                                        {sub.status}
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-3 text-[11px] text-zinc-300">
                                <div>
                                    <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">Started</div>
                                    {fmtDate(sub.current_period_start || sub.created_at)}
                                </div>
                                <div>
                                    <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">
                                        {sub.cancel_at_period_end ? "Ends" : "Renews"}
                                    </div>
                                    {fmtDate(sub.current_period_end)}
                                </div>
                            </div>
                        </div>
                    )}
                    <div data-testid="active-packs-list" className="space-y-1.5">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 flex items-baseline justify-between gap-2 flex-wrap">
                            <span>Session Packs <span className="text-zinc-600 normal-case tracking-normal text-[10px]">· one-time charges (separate from weekly subs)</span></span>
                            <div className="flex items-center gap-3 flex-wrap">
                                <button
                                    type="button"
                                    data-testid="lead-detail-view-attendance"
                                    onClick={openAttendance}
                                    disabled={ensuring != null}
                                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase border border-zinc-700 bg-zinc-900/40 text-zinc-200 px-2.5 py-1 hover:border-brand-red hover:text-white transition-colors normal-case tracking-normal disabled:opacity-60"
                                    title="See every session this athlete has attended"
                                    style={{ letterSpacing: "0.15em" }}
                                >
                                    {ensuring === "attendance" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarDays className="w-3 h-3" />} View attendance
                                </button>
                                <button
                                    type="button"
                                    data-testid="lead-detail-log-past-session"
                                    onClick={openLog}
                                    disabled={ensuring != null}
                                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase border border-amber-600/60 bg-amber-950/30 text-amber-200 px-2.5 py-1 hover:bg-amber-900/50 transition-colors normal-case tracking-normal disabled:opacity-60"
                                    title="Record a session that already happened but wasn't on the schedule"
                                    style={{ letterSpacing: "0.15em" }}
                                >
                                    {ensuring === "log" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ClipboardList className="w-3 h-3" />} Log past session
                                </button>
                                <span className="font-mono text-brand-red text-sm tracking-normal">
                                    {totalRemaining} <span className="text-zinc-500 text-[10px]">sessions left total</span>
                                </span>
                            </div>
                        </div>
                            {packs.length === 0 ? (
                                <div className="border border-zinc-900 bg-black/40 px-3 py-2.5 text-xs text-zinc-500">
                                    No active pack on file. Logging a session will open an IOU until a pack is purchased.
                                </div>
                            ) : packs.map((p) => {
                                const used = Number(p.sessions_used || 0);
                                const total = Number(p.sessions_total || 0);
                                const remaining = Math.max(0, total - used);
                                const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
                                return (
                                    <div
                                        key={p.pack_id}
                                        data-testid={`pack-row-${p.pack_id}`}
                                        className="border border-zinc-900 bg-black/40 px-3 py-2.5"
                                    >
                                        <div className="flex items-center justify-between gap-2 text-sm">
                                            <span className="text-white truncate">
                                                {p.label || p.pack_key || "Session pack"}
                                            </span>
                                            <span className="text-zinc-300 whitespace-nowrap font-mono">
                                                {remaining}/{total} left
                                            </span>
                                        </div>
                                        <div className="h-1 bg-zinc-900 rounded mt-2 overflow-hidden">
                                            <div className="h-full bg-brand-red" style={{ width: `${pct}%` }} />
                                        </div>
                                        {p.expires_at && (
                                            <div className="text-[10px] text-zinc-500 mt-1.5">
                                                Expires {fmtDate(p.expires_at)}
                                            </div>
                                        )}
                                        <PackAutoRenewToggle userId={userId} pack={p} />
                                    </div>
                                );
                            })}
                        </div>
                </div>
            {logOpen && athletes.length > 0 && (
                <LogPastAttendanceModal
                    athletes={athletes}
                    onClose={() => setLogOpen(false)}
                    onLogged={() => {
                        setLogOpen(false);
                        window.dispatchEvent(new Event("maxsc:lead-meta-refresh"));
                    }}
                />
            )}
        </div>
    );
}


/**
 * SetupCardForClientButton — moved to /components/coach/SetupCardForClientButton.jsx
 * so it can be reused on the Athlete Dossier billing tab. Same backend
 * endpoints, same UX. Kept this file's import + usage below.
 */


export default function LeadDetailBilling({ lead, isOwner, onMetaChanged, onOpenEnroll, onOpenCharge }) {
    return (
        <>
            <AutoRenewsPanel userId={lead.user_id} />
            <CurrentBillingPanel lead={lead} />
            {/* Card on file — top of tab (2026-06): was buried at the bottom
                inside "Other billing actions"; owner couldn't find where to
                add a card for a profile. */}
            <div className="px-6 py-5 border-b border-zinc-900" data-testid="billing-card-on-file-section">
                <SetupCardForClientButton clientUserId={lead.user_id} clientEmail={lead.email} onChanged={() => onMetaChanged?.()} />
            </div>
            <NoSubscriptionBanner userId={lead.user_id} />
            <ActiveSubscriptionsPanel userId={lead.user_id} />
            <StripeSubscriptionsInspector userId={lead.user_id} />
            <div className="px-6 py-5 border-b border-zinc-900 bg-gradient-to-b from-brand-red/5 to-transparent" data-testid="lead-detail-charge-and-subscribe">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-1 flex items-center gap-2">
                    Charge a package and / or start a subscription <span className="text-[9px] text-brand-red tracking-[0.2em] normal-case">· unified</span>
                </div>
                <p className="text-[11px] text-zinc-500 mb-3">
                    Pick a block, then optionally pick a package and/or a subscription mode. The cart at the bottom shows the combined total before you charge.
                </p>
                <ChargeAndSubscribePanel
                    userId={lead.user_id}
                    athletes={Array.isArray(lead.athletes) ? lead.athletes : []}
                    onChanged={() => onMetaChanged?.()}
                />
            </div>
            <div className="px-6 py-5 border-b border-zinc-900 bg-gradient-to-b from-zinc-900/40 to-transparent">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3">Other billing actions</div>
                <div className="grid sm:grid-cols-2 gap-3">
                    {isOwner && (
                        <button
                            type="button"
                            onClick={onOpenEnroll}
                            data-testid="open-enroll-modal"
                            className="group text-left p-3.5 border border-brand-red bg-brand-red/10 hover:bg-brand-red hover:text-white transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-brand-red group-hover:text-white" />
                                <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-brand-red group-hover:text-white">
                                    Email enrollment link
                                </div>
                            </div>
                            <p className="text-[11px] text-zinc-300 group-hover:text-white/90 mt-1.5 leading-snug">
                                Send the parent a one-tap <strong>Enroll &amp; pay</strong> link (Stripe Checkout).
                            </p>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onOpenCharge}
                        data-testid="open-charge-modal"
                        className="group text-left p-3.5 border border-zinc-800 hover:border-brand-red hover:bg-zinc-900 transition-colors"
                    >
                        <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white group-hover:text-brand-red">
                            One-off charge
                        </div>
                        <p className="text-[11px] text-zinc-400 group-hover:text-zinc-200 mt-1.5 leading-snug">
                            Drop-ins, late fees, extras. Uses the saved card on file.
                        </p>
                    </button>
                </div>
                {isOwner && (
                    <div className="mt-4">
                        <DemoModeToggle userId={lead.user_id} onChanged={() => onMetaChanged?.()} />
                    </div>
                )}
                {!isOwner && (
                    <p className="text-[10px] tracking-wider text-zinc-500 mt-3">
                        Only owners can email enrollment links. Ask the owner to set up the parent.
                    </p>
                )}
            </div>
            <PaymentHistoryPanel userId={lead.user_id} />
        </>
    );
}
